// Pezzi condivisi fra allenamenti e giornate partite.
import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { currentIntlLocale } from "../../lib/i18n";
import type { Session } from "../../types";

export function SessionDate({ date }: { date: Date }) {
  const locale = currentIntlLocale();
  return (
    <div className="w-11 shrink-0 text-center">
      <span className="eb-data block text-[13px] text-[hsl(var(--faint))]">{date.toLocaleDateString(locale, { weekday: "short" }).replace(".", "")}</span>
      <span className="eb-num block text-[34px] pr-0.5">{date.getDate()}</span>
      <span className="eb-data block text-[13px] text-[hsl(var(--faint))]">{date.toLocaleDateString(locale, { month: "short" }).replace(".", "")}</span>
    </div>
  );
}

const DOT = { present: "dot-ok", late: "dot-warn", absent: "dot-danger", pending: "dot-muted" } as const;

export function CountMark({ tone, value, label }: { tone: keyof typeof DOT; value: number; label: string }) {
  return (
    <span className="flex flex-col items-center min-w-[38px]">
      <span className="eb-num text-[26px] pr-0.5">{value}</span>
      <span className="inline-flex items-center gap-1 text-[13px] text-[hsl(var(--faint))]">
        <span className={cn("eb-dot", DOT[tone])} />
        {label}
      </span>
    </span>
  );
}

export function AttendanceBar({ present, late, absent }: { present: number; late: number; absent: number }) {
  return (
    <span className="flex h-1 gap-0.5 bg-raised" aria-hidden>
      {present > 0 && <span className="h-full bg-[hsl(var(--ok))]" style={{ width: `${present}%` }} />}
      {late > 0 && <span className="h-full bg-[hsl(var(--warn))]" style={{ width: `${late}%` }} />}
      {absent > 0 && <span className="h-full bg-[hsl(var(--danger))]" style={{ width: `${absent}%` }} />}
    </span>
  );
}

export function StatTileLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[13px] text-[hsl(var(--faint))] leading-tight">{children}</p>;
}

// Le sessioni divise fra in arrivo (dalla piu' vicina) e concluse.
export function TimeSections<T extends Pick<Session, "starts_at">>({
  items,
  render,
}: {
  items: T[];
  render: (item: T) => React.ReactNode;
}) {
  const { t } = useTranslation("sessions");
  const now = Date.now();
  const time = (item: T) => new Date(item.starts_at).getTime();
  const upcoming = items.filter((item) => time(item) >= now).sort((a, b) => time(a) - time(b));
  const past = items.filter((item) => time(item) < now);
  return (
    <>
      {([[t("events.sectionUpcoming"), upcoming], [t("events.sectionPast"), past]] as const).map(([label, rows]) =>
        rows.length > 0 ? (
          <section key={label} className="space-y-2.5">
            <h2 className="text-[17px] font-semibold px-1 pt-2">{label}</h2>
            {rows.map(render)}
          </section>
        ) : null,
      )}
    </>
  );
}
